import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import AppSnackbar from "layouts/notifications/Snackbar";
import { resetPassword, getUserInfo } from "services/authService";
import { useTranslation } from "hooks/useTranslation";
import { translateApiMessage } from "utils/translateApiMessage";

function resolveUserId(user) {
  if (user == null) return null;
  if (typeof user === "object") return user.id ?? null;
  return user;
}

function validateForm(form, t, requireOldPassword) {
  const errors = {};

  if (requireOldPassword && !form.old_password) {
    errors.old_password = t(
      "superAdmin.modals.resetPasswordForm.validation.oldPasswordRequired",
      "Old Password is required"
    );
  }
  if (!form.password) {
    errors.password = t(
      "superAdmin.modals.resetPasswordForm.validation.newPasswordRequired",
      "New Password is required"
    );
  }
  if (!form.confirm_password) {
    errors.confirm_password = t(
      "superAdmin.modals.resetPasswordForm.validation.confirmPasswordRequired",
      "Confirm Password is required"
    );
  }
  if (form.password && form.confirm_password && form.password !== form.confirm_password) {
    errors.confirm_password = t(
      "superAdmin.modals.resetPasswordForm.validation.passwordsDontMatch",
      "Passwords do not match"
    );
  }
  return errors;
}

export default function ResetPassword({ open, onClose, user, onSuccess, title }) {
  const { t } = useTranslation();
  const modalTitle = title || t("superAdmin.modals.resetPassword", "Reset Password");
  const [targetUserId, setTargetUserId] = useState(null);
  const [isAdminReset, setIsAdminReset] = useState(false);
  const [form, setForm] = useState({
    old_password: "",
    password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});

  const [snackbars, setSnackbars] = useState([]);

  const showSnackbar = (type, message) => {
    const id = Date.now() + Math.random();
    setSnackbars((prev) => [...prev, { id, type, message, open: true }]);

    setTimeout(() => closeSnackbar(id), 4000);
  };

  const closeSnackbar = (id) => {
    setSnackbars((prev) => prev.filter((snack) => snack.id !== id));
  };

  useEffect(() => {
    if (open) {
      const loggedInUser = getUserInfo();
      const loggedInUserId = loggedInUser?.id ?? localStorage.getItem("user_id") ?? null;

      const resolvedId = resolveUserId(user) ?? loggedInUserId ?? localStorage.getItem("user_id");

      const normalizedId = resolvedId != null && resolvedId !== "" ? String(resolvedId) : null;

      setTargetUserId(normalizedId);
      setIsAdminReset(
        normalizedId != null &&
          loggedInUserId != null &&
          String(normalizedId) !== String(loggedInUserId)
      );
      setForm({
        old_password: "",
        password: "",
        confirm_password: "",
      });
      setErrors({});
    }
  }, [open, user]);

  const handleResetPassword = async () => {
    if (!targetUserId) {
      showSnackbar(
        "error",
        t("superAdmin.modals.resetPasswordForm.apiErrors.userIdRequired", "User id is required")
      );
      return;
    }

    const requireOldPassword = !isAdminReset;
    const validationErrors = validateForm(form, t, requireOldPassword);
    if (Object.keys(validationErrors).length === 0) {
      const result = await resetPasswordForm(form, requireOldPassword);
      if (result) {
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 1000);
      }
    } else {
      setErrors(validationErrors);
      const firstError = Object.values(validationErrors)[0];
      showSnackbar("error", firstError);
    }
  };

  const handleCancel = () => {
    setForm({
      old_password: "",
      password: "",
      confirm_password: "",
    });
    setErrors({});
    onClose();
  };

  async function resetPasswordForm(form, requireOldPassword) {
    const formObj = {
      user_id: targetUserId,
      new_password: form.password,
    };

    if (requireOldPassword) {
      formObj.old_password = form.old_password;
    }

    const result = await resetPassword(formObj);
    if (result.status == "success") {
      showSnackbar(
        "success",
        t("superAdmin.notifications.passwordReset", "Password reset successfully")
      );
      return true;
    }
    showSnackbar(
      "error",
      translateApiMessage(
        result.message,
        result.code,
        t,
        t("superAdmin.notifications.passwordResetFailed", "Unable to reset password")
      )
    );
    return false;
  }

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <MDBox variant="gradient" borderRadius="lg" mb={1} textAlign="center">
          <MDTypography variant="h4" fontWeight="medium" mt={3}>
            {modalTitle}
          </MDTypography>
        </MDBox>
        <MDBox pb={3} px={5}>
          <MDBox component="form" role="form">
            {isAdminReset && (
              <MDBox mb={3}>
                <MDTypography variant="body2" color="text">
                  {t(
                    "superAdmin.modals.resetPasswordForm.adminResetHint",
                    "Set a new password for the selected user. They will use this password on their next sign-in."
                  )}
                </MDTypography>
              </MDBox>
            )}
            {!isAdminReset && (
              <MDBox mb={4}>
                <MDInput
                  type="password"
                  name="old_password"
                  label={t(
                    "superAdmin.modals.resetPasswordForm.fields.oldPassword",
                    "Old Password"
                  )}
                  variant="standard"
                  value={form.old_password}
                  fullWidth
                  onChange={(e) => setForm({ ...form, old_password: e.target.value })}
                  error={Boolean(errors.old_password)}
                />
              </MDBox>
            )}
            <MDBox mb={4}>
              <MDInput
                type="password"
                name="password"
                label={t("superAdmin.modals.resetPasswordForm.fields.newPassword", "New Password")}
                variant="standard"
                value={form.password}
                fullWidth
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                error={Boolean(errors.password)}
              />
            </MDBox>
            <MDBox mb={4}>
              <MDInput
                type="password"
                name="confirm_password"
                label={t(
                  "superAdmin.modals.resetPasswordForm.fields.confirmNewPassword",
                  "Confirm New Password"
                )}
                variant="standard"
                value={form.confirm_password}
                fullWidth
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                error={Boolean(errors.confirm_password)}
              />
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDButton variant="gradient" color="info" onClick={handleResetPassword}>
                {t("superAdmin.buttons.reset", "Reset")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Dialog>
      {ReactDOM.createPortal(
        snackbars.map((snack) => (
          <AppSnackbar
            key={snack.id}
            type={snack.type}
            message={snack.message}
            open={snack.open}
            onClose={() => closeSnackbar(snack.id)}
          />
        )),
        document.body
      )}
    </>
  );
}

ResetPassword.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  onSuccess: PropTypes.func,
  title: PropTypes.string,
};

ResetPassword.defaultProps = {
  user: null,
  onSuccess: undefined,
  title: undefined,
};
